import React, { createContext, useState, useCallback, useEffect } from 'react'
import useProfileData from '../hooks/useProfileData'

export const UserContext = createContext()

// Cache keys for localStorage
const USER_CACHE_KEYS = {
  USER_ROLE: 'user_role',
  USER_ACCESS_DATA: 'user_access_data',
  USER_STORES: 'user_stores'
}

// Cache utility functions
const cacheUserData = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify(data))
  } catch (error) {
    console.warn('Failed to cache user data:', error)
  }
}

const getCachedUserData = (key) => {
  try {
    const cached = localStorage.getItem(key)
    return cached ? JSON.parse(cached) : null
  } catch (error) {
    console.warn('Failed to get cached user data:', error)
    return null
  }
}

const clearCachedUserData = (key) => {
  try {
    localStorage.removeItem(key)
  } catch (error) {
    console.warn('Failed to clear cached user data:', error)
  }
}

const UserProvider = ({ children }) => {
  const { graphData, profileImageData, cognitoData, accessToken } = useProfileData()

  // Initialize state with cached data if available
  const [userStores, setUserStores] = useState(() => {
    return getCachedUserData(USER_CACHE_KEYS.USER_STORES) || []
  })

  const [userRole, setUserRole] = useState(() => {
    return getCachedUserData(USER_CACHE_KEYS.USER_ROLE) || ''
  })

  const [userAccessData, setUserAccessData] = useState(() => {
    return getCachedUserData(USER_CACHE_KEYS.USER_ACCESS_DATA) || null
  })

  const [isLoadingAccess, setIsLoadingAccess] = useState(false)
  const [accessError, setAccessError] = useState(null)

  // Cache data when it changes
  useEffect(() => {
    if (userRole) {
      cacheUserData(USER_CACHE_KEYS.USER_ROLE, userRole)
    }
  }, [userRole])

  useEffect(() => {
    if (userAccessData) {
      cacheUserData(USER_CACHE_KEYS.USER_ACCESS_DATA, userAccessData)
    }
  }, [userAccessData])

  useEffect(() => {
    if (userStores && userStores.length > 0) {
      cacheUserData(USER_CACHE_KEYS.USER_STORES, userStores)
    }
  }, [userStores])

  // Function to update user stores from API response
  const updateUserStores = useCallback((storeIds) => {
    if (Array.isArray(storeIds)) {
      setUserStores(storeIds)
      cacheUserData(USER_CACHE_KEYS.USER_STORES, storeIds)
    } else {
      setUserStores([])
      clearCachedUserData(USER_CACHE_KEYS.USER_STORES)
    }
  }, [])

  // Function to update user role with caching
  const updateUserRole = useCallback((role) => {
    setUserRole(role)
    if (role) {
      cacheUserData(USER_CACHE_KEYS.USER_ROLE, role)
    } else {
      clearCachedUserData(USER_CACHE_KEYS.USER_ROLE)
    }
  }, [])

  // Function to update full user access data
  const updateUserAccessData = useCallback((accessData) => {
    setUserAccessData(accessData)

    if (accessData) {
      cacheUserData(USER_CACHE_KEYS.USER_ACCESS_DATA, accessData)
    } else {
      clearCachedUserData(USER_CACHE_KEYS.USER_ACCESS_DATA)
    }

    // Extract stores from the access data
    if (accessData?.message?.stores) {
      updateUserStores(accessData.message.stores)
    }

    // Get role from API response
    if (accessData?.message?.role) {
      updateUserRole(accessData.message.role)
    }
  }, [updateUserStores, updateUserRole])

  // Function to validate user access
  const validateUserAccess = useCallback((accessData) => {
    if (!accessData?.message) {
      return false
    }

    const { stores, permissions, accessibleScreens } = accessData.message

    // Check if user has at least one store and some permissions
    const hasStoreAccess = stores && stores.length > 0
    const hasPermissions = (permissions && Object.keys(permissions).length > 0) ||
      (accessibleScreens && accessibleScreens.length > 0)

    return hasStoreAccess && hasPermissions
  }, [])

  // Enhanced logout function - comprehensive cleanup
  const logoutUser = useCallback(() => {
    console.log('Starting user context logout cleanup...')

    // Clear all user-related state
    setUserStores([])
    setUserRole('')
    setUserAccessData(null)
    setIsLoadingAccess(false)
    setAccessError(null)

    // Clear cached data
    Object.values(USER_CACHE_KEYS).forEach(key => {
      clearCachedUserData(key)
    })

    console.log('User context state cleared successfully')
  }, [])

  // Clear all user data (for logout) - keeping original method for backward compatibility
  const clearUserData = useCallback(() => {
    logoutUser()
  }, [logoutUser])

  // Function to check if user has access to a specific feature
  const hasAccessToFeature = useCallback((featureId) => {
    if (!userAccessData || !userAccessData.message) {
      return false
    }

    const { permissions, accessibleScreens } = userAccessData.message

    // Check if feature is in accessibleScreens array
    if (accessibleScreens && accessibleScreens.includes(featureId)) {
      return true
    }

    // Also check permissions object for access property
    if (permissions && permissions[featureId] && permissions[featureId].access === true) {
      return true
    }

    return false
  }, [userAccessData])

  // Function to get user permissions for a specific feature
  const getUserPermissions = useCallback((featureId) => {
    if (!userAccessData || !userAccessData.message || !userAccessData.message.permissions) {
      return null
    }

    return userAccessData.message.permissions[featureId] || null
  }, [userAccessData])

  // Function to reset loading states
  const resetLoadingStates = useCallback(() => {
    setIsLoadingAccess(false)
    setAccessError(null)
  }, [])

  // Enhanced update user access data with validation
  const enhancedUpdateUserAccessData = useCallback((accessData) => {
    updateUserAccessData(accessData)

    // Validate access after setting data
    if (accessData && !validateUserAccess(accessData)) {
      setAccessError('No access permissions found')
    } else {
      setAccessError(null)
    }
  }, [updateUserAccessData, validateUserAccess])

  // Initialize user data from cache if available
  const initializeUserData = useCallback(async () => {
    const cachedRole = getCachedUserData(USER_CACHE_KEYS.USER_ROLE)
    const cachedAccessData = getCachedUserData(USER_CACHE_KEYS.USER_ACCESS_DATA)
    const cachedStores = getCachedUserData(USER_CACHE_KEYS.USER_STORES)

    // Restore cached data if current state is empty
    if (cachedRole && !userRole) {
      setUserRole(cachedRole)
    }

    if (cachedAccessData && !userAccessData) {
      setUserAccessData(cachedAccessData)
      // If restoring from cache, also restore the role from cached access data
      if (cachedAccessData?.message?.role && !userRole && !cachedRole) {
        setUserRole(cachedAccessData.message.role)
      }
    }

    if (cachedStores && userStores.length === 0) {
      setUserStores(cachedStores)
    }
  }, [userRole, userAccessData, userStores])

  const contextValue = {
    // Profile data from useProfileData hook
    graphData,
    profileImageData,
    cognitoData,
    accessToken,

    // User access state
    userRole,
    userStores,
    userAccessData,
    isLoadingAccess,
    accessError,

    // Original setters for backward compatibility
    setUserStores: updateUserStores,
    setUserAccessData: updateUserAccessData,
    setUserRole: updateUserRole,
    setIsLoadingAccess,
    setAccessError,
    clearUserData,
    validateUserAccess,

    // Enhanced methods with logout functionality
    logoutUser,
    hasAccessToFeature,
    getUserPermissions,
    resetLoadingStates,
    updateUserAccessData: enhancedUpdateUserAccessData,
    initializeUserData,

    // Backward compatibility
    isLoadingGroups: isLoadingAccess,
    groupsError: accessError
  }

  return (
    <UserContext.Provider value={contextValue}>
      {children}
    </UserContext.Provider>
  )
}

export default UserProvider